import {
    AppBar,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import {
    CloseOutlined,
    PlusCircleOutlined,
    TranslationOutlined,
} from '@ant-design/icons';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { FormErrors, FormValues } from '../../form/types';
import { useTranslation } from '../../../contexts/TranslationContext';
import { reindex, reorder } from '../../../utils/cms';
import ModalEntry from '../../form/fields/modal-entry';
import { FormField } from '../../../api/types/form';
import { makeHash } from '../../../utils/static';
import ModalTranslationLanguageDialog from '../../dialog/ModalTranslationLanguageDialog';
import { TranslateDialogRef } from '../../dialog/TranslateDialog';
import CmsContentConnectContextProvider from '../../../contexts/CmsContentConnectContext';

interface ModalEntryListProps {
    open: boolean;
    items: FormValues;
    fieldConfig: FormField;
    timeCheckItems?: string;
    value?: any;
    onClose?: (newValues: any) => void;
    error?: FormErrors;
    hasTimePublishing?: boolean;
    sortable?: boolean;
    inCmsEdit?: boolean;
    locale?: string;
    formType?: string;
}

export default function ModalEntryList({
    open,
    fieldConfig,
    value,
    onClose,
    error,
    hasTimePublishing = false,
    sortable = false,
    inCmsEdit = false,
    locale,
    formType,
}: ModalEntryListProps) {
    const translationRef = useRef<TranslateDialogRef>();

    const theme = useTheme();
    const { translate } = useTranslation();

    const [errors, setErrors] = useState<FormErrors | undefined>();
    const [rows, setRows] = useState<Array<FormValues>>(value);

    const { attr, name, label } = fieldConfig;

    const translatable = Boolean(attr?.translatable);

    useEffect(() => {
        if (makeHash(errors) !== makeHash(error)) {
            setErrors(error);
        }
    }, [error]);

    const changeRowValue = (index: number, newValues: any) => {
        setRows(draft => {
            const temp = Array.from(draft ?? []);
            temp[index] = newValues;
            return temp;
        });
    };

    const addRow = (values: FormValues = {}) => {
        setRows(draft => {
            const temp = Array.from(draft ?? []);
            temp.push(values);
            return temp;
        });
    };

    const removeRow = (index: number) => {
        setRows(draft => {
            const temp = Array.from(draft);
            temp.splice(index, 1);

            return temp;
        });
    };

    const copyRow = (index: number) => {
        setRows(draft => {
            const temp = Array.from(draft);
            const element = temp.at(index);
            if (element) {
                return [...temp.slice(0, index), element, ...temp.slice(index)];
            }
            return temp;
        });
    };

    const onDragEnd = ({ destination, source }: DropResult) => {
        if (!destination) return;
        setErrors(draft => {
            if (draft && draft[source.index]) {
                draft[destination.index] = draft[source.index];
                delete draft[source.index];
            }
            return draft;
        });
        setRows(draft => {
            const reordered = reorder(draft, source.index, destination.index);
            if (sortable) {
                return reindex(reordered);
            }
            return reordered;
        });
    };

    useEffect(() => {
        if (translatable) {
            const firstRow = rows?.at(0);
            if (firstRow) {
                translationRef.current?.registerUpdateCallback(
                    (translationResponse, targetLocale: string) => {
                        addRow({
                            locale: targetLocale,
                            content: translationResponse,
                        });
                    },
                );
                translationRef.current?.setTranslateObj({
                    text: firstRow.content,
                    sourceLocale: firstRow.locale,
                });
            }
        }
    }, [rows, translatable, translationRef.current]);

    const onModalClose = () => {
        onClose && onClose(rows);
    };

    const buildTimePublishingFields = (
        rowValues: FormValues,
    ): Array<FormField> => {
        return [
            {
                type: 'datetime',
                name: 'startDate',
                label: 'pages.cms.pages.time_publishing.start_date',
                required: true,
                value: rowValues['startDate'],
            },
            {
                type: 'datetime',
                name: 'endDate',
                label: 'pages.cms.pages.time_publishing.end_date',
                required: true,
                value: rowValues['endDate'],
            },
        ];
    };

    const renderModalEntry = (rowValues: FormValues, index: number) => {
        return (
            <ModalEntry
                key={index}
                index={index}
                fieldConfig={fieldConfig}
                values={rowValues}
                removeRow={removeRow}
                copyRow={copyRow}
                onValueChange={newValues => changeRowValue(index, newValues)}
                errors={errors ? errors[index] : undefined}
                additionalFields={
                    hasTimePublishing
                        ? buildTimePublishingFields(rowValues)
                        : []
                }
                withConnect={inCmsEdit}
                locale={locale}
                connectOptions={{
                    name: 'partnerConnect',
                    label: 'pages.cms.pages.partner.connect',
                    matcher: {
                        image: 'KeyVisual',
                        logo: 'Logo',
                        link: 'Link',
                    },
                }}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onModalClose}
            fullScreen
            disableScrollLock={false}
            hideBackdrop>
            <AppBar
                color={theme.palette.mode === 'dark' ? 'default' : 'inherit'}
                position="relative">
                <Toolbar>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flex={1}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="h5">
                                {translate(label)}
                            </Typography>
                            <Button
                                startIcon={<PlusCircleOutlined />}
                                onClick={() => addRow()}>
                                <Typography textTransform="none" lineHeight={1}>
                                    {translate(label)}
                                </Typography>
                            </Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            {translatable && (
                                <Button
                                    color="info"
                                    startIcon={<TranslationOutlined />}
                                    onClick={() =>
                                        translationRef.current?.open()
                                    }>
                                    <Typography
                                        textTransform="none"
                                        lineHeight={1}>
                                        {translate(
                                            'form.general.field_context.translate_val',
                                        )}
                                    </Typography>
                                </Button>
                            )}
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={onModalClose}
                                aria-label="close">
                                <CloseOutlined />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>
            <DialogContent>
                <CmsContentConnectContextProvider name={formType}>
                    {/* @ts-ignore */}
                    <DragDropContext onDragEnd={onDragEnd} enableDefaultSensors>
                        {/* @ts-ignore */}
                        <Droppable
                            droppableId={`modal-entry-sorted-${name}`}
                            mode="standard"
                            direction="vertical">
                            {provided => (
                                <Stack
                                    component="div"
                                    height="100%"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{ mb: 4 }}>
                                    {rows?.map(renderModalEntry)}
                                </Stack>
                            )}
                        </Droppable>
                    </DragDropContext>
                </CmsContentConnectContextProvider>
            </DialogContent>
            {translatable && (
                <ModalTranslationLanguageDialog dialogRef={translationRef} />
            )}
        </Dialog>
    );
}
